import React, { useCallback, useMemo, useState } from 'react'
import Modal from 'react-modal'
import classNames from 'classnames'
import { useSelector, useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import _ from 'lodash'

import api from 'Src/api'

import CodeMirrorComponent from 'Src/components/Codemirror'
import { getAccountDetails } from 'Selectors/account'
import { fetchAccount } from 'Actions/account'
import hasPermission from 'LegacyUtils/hasPermission'
import { getCurrentUserPermissions } from 'Selectors/users'

const PATCH_ACCOUNT_URL = 'v1/account/'

const updateAccount = async (data = {}) => {
  return await api.patch(PATCH_ACCOUNT_URL, data)
}

const DefaultComponent = ({ account }) => {
  const dispatch = useDispatch()

  const {
    handleSubmit,
    register,
    formState: { errors }
  } = useForm({
    defaultValues: {
      company_name: account.company_name
    }
  })

  const onSubmit = async (data) => {
    const { company_name } = data
    try {
      await updateAccount({ company_name })
      dispatch(fetchAccount())
      alert('Account settings updated successfully')
    } catch (err) {
      console.error('Error while updating account settings', err)
      alert('Failed to update account settings')
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className='form-group'>
        <label>Company Name</label>
        <input
          className={classNames('form-control', { 'is-invalid': errors.company_name })}
          name='company_name'
          {...register('company_name', { required: 'Company Name is required' })}
        />
        {errors.company_name && <small className='form-text text-danger'>{errors.company_name.message}</small>}
      </div>
      <button className='btn btn-sm btn-primary' type='submit'>
        Update
      </button>
    </form>
  )
}

const JSONEditor = ({ account }) => {
  const editableFields = _.pick(account, ['company_name', 'settings'])
  const inptuValue = JSON.stringify(editableFields, null, 2)

  const dispatch = useDispatch()

  const [value, setValue] = useState(JSON.stringify(editableFields, null, 2))

  const update = async () => {
    let data
    try {
      data = JSON.parse(value)
    } catch (err) {
      console.error('Unable to parse json', err)
      alert('Invalid JSON')
      return
    }

    try {
      await updateAccount(data)
      dispatch(fetchAccount())
      alert('Account settings updated successfully')
    } catch (err) {
      console.error('Unable to update settings via json editor', err)
      alert('Error while updating account settings')
    }
  }

  const handleChange = useCallback((val) => {
    setValue(val)
  }, [])

  return (
    <React.Fragment>
      <CodeMirrorComponent value={inptuValue} onChange={handleChange} />
      <button className='btn btn-sm btn-primary mt-2' type='button' onClick={update}>
        Update
      </button>
    </React.Fragment>
  )
}

const ALL_TABS = [
  {
    id: 'default',
    name: 'Basic',
    component: DefaultComponent
  },
  {
    id: 'json',
    name: 'Advanced',
    component: JSONEditor,
    permissions: ['developer_settings_modify']
  }
]

const EditAccountModal = ({ isOpen, hide }) => {
  const [activeTab, setActiveTab] = useState('default')
  const account = useSelector(getAccountDetails)
  const userPermissions = useSelector(getCurrentUserPermissions)

  const onClose = useCallback(hide, [hide])

  const onTabClick = (e, id) => {
    e.preventDefault()
    setActiveTab(id)
  }

  const tabs = useMemo(() => {
    return ALL_TABS.filter((tab) => {
      if (!tab.permissions) {
        return true
      }

      return hasPermission(userPermissions, tab.permissions)
    })
  }, [userPermissions])

  return (
    <div>
      <Modal isOpen={isOpen} contentLabel='Update Account Settings Modal' ariaHideApp={false} onRequestClose={onClose}>
        <ul className='nav nav-tabs mb-2'>
          {tabs.map((tab) => {
            return (
              <li className='nav-item' key={tab.id + 'navItem'}>
                <a
                  href='#'
                  className={classNames('nav-link', { active: activeTab === tab.id })}
                  onClick={(e) => onTabClick(e, tab.id)}
                >
                  {tab.name}
                </a>
              </li>
            )
          })}
        </ul>
        <div className='tab-content'>
          {tabs.map((tab) => {
            const TabComponent = tab.component

            if (activeTab !== tab.id) {
              return null
            }

            return (
              <div className='tab-pane active' key={tab.id + 'tabConent'}>
                <TabComponent account={account} />
              </div>
            )
          })}
        </div>
      </Modal>
    </div>
  )
}

export default EditAccountModal
